/**
 * Re-carve a Voronoi maze from pre-computed templates.
 *
 * Rebuilds the full adjacency graph from shared polygon edges,
 * runs a randomized DFS to create a new spanning tree, optionally
 * adds extra passages based on difficulty, and picks new entry/exit.
 *
 * Pure function — no side effects, returns a new VoronoiMazeData.
 */

import type {
  VoronoiMazeData,
  VoronoiRegion,
  Position,
} from '../types/maze-types';
import type { GameDifficulty } from '@/contexts/games/types';

export interface RecarveOptions {
  /** Difficulty controls extra passage percentage */
  difficulty: GameDifficulty;
  /**
   * Narrowest passage the carve may open, in the SAME units as the region
   * coordinates (canvas px once the loader has scaled them). A shared edge
   * shorter than this stays a wall — Voronoi cells share plenty of 2–8 px
   * edges and a passage that short is invisible and un-drawable. Default:
   * relative to the tessellation (see defaultMinPassageWidth) so a dense
   * "expert" maze is not walled shut; pass 0 to disable (abstract fixtures).
   */
  minPassageWidth?: number;
  /** Internal: retry count for unsolvable mazes */
  _retryCount?: number;
}

/** Upper bound of the default narrowest allowed passage (canvas px). */
export const MIN_PASSAGE_PX = 14;
/** Lower bound — below this a gap is invisible at any density. */
export const MIN_PASSAGE_FLOOR_PX = 6;

/**
 * Default minimum passage for a tessellation: 45 % of the MEDIAN shared-edge
 * length, clamped to [MIN_PASSAGE_FLOOR_PX, MIN_PASSAGE_PX]. Cuts only the
 * sliver tail of the edge-length distribution (≈ the shortest tenth) at every
 * density: ~14 px on an easy maze with 35 px edges, ~6 px on an expert maze
 * whose edges are 13 px — an absolute 14 px there walled whole limbs shut.
 */
export function defaultMinPassageWidth(regions: VoronoiRegion[], adjacency: Map<number, Set<number>>): number {
  const regionById = new Map<number, VoronoiRegion>();
  for (const r of regions) regionById.set(r.id, r);
  const lens: number[] = [];
  for (const [id, neighbors] of adjacency) {
    const a = regionById.get(id);
    if (!a) continue;
    for (const nid of neighbors) {
      if (nid < id) continue;
      const b = regionById.get(nid);
      if (!b) continue;
      const len = computeSharedEdgeLength(a, b);
      if (len > 0) lens.push(len);
    }
  }
  if (lens.length === 0) return 0;
  lens.sort((x, y) => x - y);
  const median = lens[Math.floor(lens.length / 2)];
  return Math.max(MIN_PASSAGE_FLOOR_PX, Math.min(MIN_PASSAGE_PX, median * 0.45));
}

const DIFFICULTY_EXTRA_PERCENT: Record<GameDifficulty, number> = {
  easy: 0.40,
  medium: 0.20,
  hard: 0.08,
  expert: 0,
};

/**
 * Create a canonical string key for a vertex (rounded to 1 decimal).
 * Must match the renderer's rounding so shared-edge detection is consistent.
 */
function vertexKey(p: Position): string {
  return `${Math.round(p.x * 10)},${Math.round(p.y * 10)}`;
}

/**
 * Create a canonical key for an edge (order-independent).
 */
function edgeKey(a: Position, b: Position): string {
  const ka = vertexKey(a);
  const kb = vertexKey(b);
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

/**
 * Get the full geometric adjacency from regions.
 * Prefers `allNeighbors` (pre-computed by worker) over edge-based reconstruction.
 */
export function buildFullAdjacency(
  regions: VoronoiRegion[],
): Map<number, Set<number>> {
  const adjacency = new Map<number, Set<number>>();
  for (const region of regions) {
    adjacency.set(region.id, new Set());
  }

  // Use allNeighbors if available (reliable, from Voronoi ridges)
  const hasAllNeighbors = regions.some((r) => r.allNeighbors && r.allNeighbors.length > 0);

  if (hasAllNeighbors) {
    for (const region of regions) {
      const nids = region.allNeighbors ?? region.neighbors;
      for (const nid of nids) {
        if (adjacency.has(nid)) {
          adjacency.get(region.id)!.add(nid);
          adjacency.get(nid)!.add(region.id);
        }
      }
    }
    return adjacency;
  }

  // Fallback: reconstruct from shared polygon edges (less reliable due to rounding)
  const edgeToRegions = new Map<string, number[]>();

  for (const region of regions) {
    const verts = region.vertices;
    for (let i = 0; i < verts.length; i++) {
      const v1 = verts[i];
      const v2 = verts[(i + 1) % verts.length];
      const key = edgeKey(v1, v2);

      const existing = edgeToRegions.get(key);
      if (existing) {
        if (!existing.includes(region.id)) {
          existing.push(region.id);
        }
      } else {
        edgeToRegions.set(key, [region.id]);
      }
    }
  }

  for (const regionIds of edgeToRegions.values()) {
    if (regionIds.length === 2) {
      adjacency.get(regionIds[0])!.add(regionIds[1]);
      adjacency.get(regionIds[1])!.add(regionIds[0]);
    }
  }

  return adjacency;
}

/**
 * Compute shared edge length between two regions.
 * Returns the length of the longest shared edge segment (in normalized coords).
 */
function computeSharedEdgeLength(
  regionA: VoronoiRegion,
  regionB: VoronoiRegion,
): number {
  let maxLen = 0;
  const vertsA = regionA.vertices;
  const vertsB = regionB.vertices;

  for (let i = 0; i < vertsA.length; i++) {
    const a1 = vertsA[i];
    const a2 = vertsA[(i + 1) % vertsA.length];
    const ka1 = vertexKey(a1);
    const ka2 = vertexKey(a2);

    for (let j = 0; j < vertsB.length; j++) {
      const b1 = vertsB[j];
      const b2 = vertsB[(j + 1) % vertsB.length];
      const kb1 = vertexKey(b1);
      const kb2 = vertexKey(b2);

      if ((ka1 === kb1 && ka2 === kb2) || (ka1 === kb2 && ka2 === kb1)) {
        const dx = a2.x - a1.x;
        const dy = a2.y - a1.y;
        maxLen = Math.max(maxLen, Math.sqrt(dx * dx + dy * dy));
      }
    }
  }

  return maxLen;
}

/**
 * Filter adjacency to remove edges whose midpoint (between the two region sites)
 * lies outside the silhouette contour. This prevents DFS from carving passages
 * that go outside the visible shape (which the clip path would then hide,
 * making the maze unsolvable).
 *
 * Mutates `adjacency` in place.
 */
/**
 * Check if a passage between two regions would be visible after contour clipping.
 * A passage is the shared edge between two polygons. If that edge is outside
 * the contour, the clip path hides it and the passage is impassable.
 *
 * Checks: midpoint of shared edge (if found) OR midpoint of sites (fallback).
 */
function isPassageInsideContour(
  regionA: VoronoiRegion,
  regionB: VoronoiRegion,
  contour: Position[],
): boolean {
  // Try to find the shared edge midpoint (most accurate)
  for (let i = 0; i < regionA.vertices.length; i++) {
    const v1 = regionA.vertices[i];
    const v2 = regionA.vertices[(i + 1) % regionA.vertices.length];
    const ka = edgeKey(v1, v2);

    for (let j = 0; j < regionB.vertices.length; j++) {
      const bv1 = regionB.vertices[j];
      const bv2 = regionB.vertices[(j + 1) % regionB.vertices.length];
      if (edgeKey(bv1, bv2) === ka) {
        // Found shared edge — check if its midpoint is inside contour
        const mx = (v1.x + v2.x) / 2;
        const my = (v1.y + v2.y) / 2;
        return pointInContour({ x: mx, y: my }, contour);
      }
    }
  }

  // No shared edge found by vertex matching — check BOTH site midpoint
  // AND closest vertex pair midpoint
  const siteMx = (regionA.site.x + regionB.site.x) / 2;
  const siteMy = (regionA.site.y + regionB.site.y) / 2;
  if (!pointInContour({ x: siteMx, y: siteMy }, contour)) {
    return false;
  }

  // Also check: find closest vertices between the two regions
  let minDist = Infinity;
  let closestMid: Position = { x: siteMx, y: siteMy };
  for (const va of regionA.vertices) {
    for (const vb of regionB.vertices) {
      const d = (va.x - vb.x) ** 2 + (va.y - vb.y) ** 2;
      if (d < minDist) {
        minDist = d;
        closestMid = { x: (va.x + vb.x) / 2, y: (va.y + vb.y) / 2 };
      }
    }
  }
  return pointInContour(closestMid, contour);
}

function filterAdjacencyByContour(
  adjacency: Map<number, Set<number>>,
  regions: VoronoiRegion[],
  contour?: Position[],
): void {
  if (!contour || contour.length < 3) return;

  const regionById = new Map<number, VoronoiRegion>();
  for (const r of regions) regionById.set(r.id, r);

  for (const [id, neighbors] of adjacency) {
    const regionA = regionById.get(id);
    if (!regionA) continue;

    const toRemove: number[] = [];
    for (const nid of neighbors) {
      const regionB = regionById.get(nid);
      if (!regionB) continue;

      if (!isPassageInsideContour(regionA, regionB, contour)) {
        toRemove.push(nid);
      }
    }

    for (const nid of toRemove) {
      neighbors.delete(nid);
      adjacency.get(nid)?.delete(id);
    }
  }
}

/**
 * Drop adjacency pairs whose shared edge is shorter than `minWidth` so the
 * carve can never open an invisible passage. Pairs with NO detectable shared
 * edge (vertex rounding mismatch) are kept — their width is unknown, and the
 * renderer's mismatch pass opens them. Mutates `adjacency` in place.
 */
export function pruneNarrowPassages(
  adjacency: Map<number, Set<number>>,
  regions: VoronoiRegion[],
  minWidth: number,
): void {
  if (!(minWidth > 0)) return;
  const regionById = new Map<number, VoronoiRegion>();
  for (const r of regions) regionById.set(r.id, r);
  // Remember each cell's widest link so a cell that only had slivers is not
  // sealed shut (a sealed honeycomb cell looks like a rendering bug): it keeps
  // its single widest edge as a dead-end pocket instead.
  const widest = new Map<number, { nid: number; len: number }>();
  const lenOf = new Map<string, number>();
  for (const [id, neighbors] of adjacency) {
    const a = regionById.get(id);
    if (!a) continue;
    for (const nid of neighbors) {
      const b = regionById.get(nid);
      if (!b) continue;
      const len = computeSharedEdgeLength(a, b);
      lenOf.set(`${id}|${nid}`, len);
      const w = widest.get(id);
      if (!w || len > w.len) widest.set(id, { nid, len });
    }
  }
  for (const [id, neighbors] of adjacency) {
    for (const nid of [...neighbors]) {
      if (nid < id) continue; // each pair once
      const len = lenOf.get(`${id}|${nid}`) ?? 0;
      if (len > 0 && len < minWidth) {
        neighbors.delete(nid);
        adjacency.get(nid)?.delete(id);
      }
    }
  }
  for (const [id, neighbors] of adjacency) {
    if (neighbors.size > 0) continue;
    const w = widest.get(id);
    if (!w || !(w.len > 0)) continue;
    neighbors.add(w.nid);
    adjacency.get(w.nid)?.add(id);
  }
}

/**
 * Regions on the SILHOUETTE border: centroid inside the contour but the
 * polygon pokes outside it (a vertex beyond the contour) — or, without a
 * contour, a polygon edge nobody shares. This is where an entrance belongs.
 * (Voronoi cells are clipped to the canvas bbox, not to the silhouette, so the
 * old "unshared edge" test only found the 1–2 cells touching the canvas edge —
 * the entry landed in the same place every game.)
 */
export function findSilhouetteBorderRegions(
  regions: VoronoiRegion[],
  contour?: Position[],
): Set<number> {
  const out = new Set<number>();
  if (contour && contour.length > 2) {
    for (const r of regions) {
      if (r.vertices.length < 3) continue;
      const c = centroidOf(r);
      if (!pointInContour(c, contour)) continue;
      if (r.vertices.some((v) => !pointInContour(v, contour))) out.add(r.id);
    }
    if (out.size >= 2) return out;
  }
  // No contour (or a shape that never pokes out): fall back to unshared edges.
  return findBorderRegions(regions, new Map());
}

function centroidOf(r: VoronoiRegion): Position {
  const verts = r.vertices;
  if (verts.length === 0) return r.site;
  let sx = 0;
  let sy = 0;
  for (const v of verts) {
    sx += v.x;
    sy += v.y;
  }
  return { x: sx / verts.length, y: sy / verts.length };
}

/**
 * Randomized DFS (iterative) to carve a spanning tree.
 * Prefers neighbors with wider shared edges (longer shared boundary = wider passage).
 * Returns the carved adjacency (passages only).
 */
export function dfsCarve(
  adjacency: Map<number, Set<number>>,
  regions?: VoronoiRegion[],
): Map<number, Set<number>> {
  const visited = new Set<number>();
  const mazeAdj = new Map<number, Set<number>>();

  for (const id of adjacency.keys()) {
    mazeAdj.set(id, new Set());
  }

  const allIds = [...adjacency.keys()];
  if (allIds.length === 0) return mazeAdj;

  // Build region lookup for edge length computation
  const regionById = new Map<number, VoronoiRegion>();
  if (regions) {
    for (const r of regions) regionById.set(r.id, r);
  }

  const startId = allIds[Math.floor(Math.random() * allIds.length)];
  const stack = [startId];
  visited.add(startId);

  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    const neighbors = [...(adjacency.get(current) ?? [])].filter(
      (n) => !visited.has(n),
    );

    if (neighbors.length > 0) {
      let next: number;

      if (regionById.size > 0) {
        // Weighted selection: prefer neighbors with wider shared edges
        const currentRegion = regionById.get(current);
        const weighted = neighbors.map((nid) => {
          const nRegion = regionById.get(nid);
          const edgeLen = currentRegion && nRegion
            ? computeSharedEdgeLength(currentRegion, nRegion)
            : 0;
          return { nid, weight: Math.max(edgeLen, 0.001) };
        });

        // Weighted random selection (longer edges = higher probability)
        const totalWeight = weighted.reduce((s, w) => s + w.weight, 0);
        let r = Math.random() * totalWeight;
        next = weighted[weighted.length - 1].nid;
        for (const w of weighted) {
          r -= w.weight;
          if (r <= 0) { next = w.nid; break; }
        }
      } else {
        next = neighbors[Math.floor(Math.random() * neighbors.length)];
      }

      visited.add(next);
      mazeAdj.get(current)!.add(next);
      mazeAdj.get(next)!.add(current);
      stack.push(next);
    } else {
      stack.pop();
    }
  }

  return mazeAdj;
}

/**
 * Add extra passages (remove walls) to make the maze easier.
 * Mutates `mazeAdj` in place for efficiency.
 */
export function addExtraPassages(
  mazeAdj: Map<number, Set<number>>,
  fullAdj: Map<number, Set<number>>,
  extraPercent: number,
): void {
  if (extraPercent <= 0) return;

  // Collect all walls (edges in fullAdj but NOT in mazeAdj)
  const walls: [number, number][] = [];
  for (const [id, neighbors] of fullAdj) {
    for (const nid of neighbors) {
      if (id < nid && !mazeAdj.get(id)!.has(nid)) {
        walls.push([id, nid]);
      }
    }
  }

  if (walls.length === 0) return;

  const toRemove = Math.floor(walls.length * extraPercent);

  // Fisher-Yates shuffle
  for (let i = walls.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [walls[i], walls[j]] = [walls[j], walls[i]];
  }

  for (let i = 0; i < toRemove; i++) {
    const [a, b] = walls[i];
    mazeAdj.get(a)!.add(b);
    mazeAdj.get(b)!.add(a);
  }
}

/**
 * Identify border regions — those that have at least one edge not shared
 * with another region (i.e., on the silhouette boundary).
 */
function findBorderRegions(
  regions: VoronoiRegion[],
  fullAdj: Map<number, Set<number>>,
): Set<number> {
  // A border region has fewer geometric neighbors than polygon edges
  // (some edges are boundary edges with no neighbor on the other side).
  // More reliable: check if any edge is unshared.
  const edgeToRegionCount = new Map<string, number>();

  for (const region of regions) {
    const verts = region.vertices;
    for (let i = 0; i < verts.length; i++) {
      const key = edgeKey(verts[i], verts[(i + 1) % verts.length]);
      edgeToRegionCount.set(key, (edgeToRegionCount.get(key) ?? 0) + 1);
    }
  }

  const borderIds = new Set<number>();
  for (const region of regions) {
    const verts = region.vertices;
    for (let i = 0; i < verts.length; i++) {
      const key = edgeKey(verts[i], verts[(i + 1) % verts.length]);
      if ((edgeToRegionCount.get(key) ?? 0) === 1) {
        borderIds.add(region.id);
        break;
      }
    }
  }

  return borderIds;
}

/**
 * Simple point-in-polygon check (ray casting) for contour filtering.
 */
function pointInContour(point: Position, contour: Position[]): boolean {
  const { x, y } = point;
  const n = contour.length;
  let inside = false;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = contour[i].x;
    const yi = contour[i].y;
    const xj = contour[j].x;
    const yj = contour[j].y;

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Weighted random pick: probability of each candidate is proportional to its
 * `weight` (must be > 0). Returns the chosen candidate. Non-deterministic.
 */
function weightedPick<T>(candidates: T[], weightFn: (c: T) => number): T {
  const weights = candidates.map((c) => Math.max(weightFn(c), Number.EPSILON));
  const total = weights.reduce((s, w) => s + w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < candidates.length; i++) {
    r -= weights[i];
    if (r <= 0) return candidates[i];
  }
  return candidates[candidates.length - 1];
}

/**
 * Pick new entry and exit regions — freshly randomized on every carve.
 *
 * Entry: chosen UNIFORMLY at random from every border region of the silhouette,
 *   so the entrance moves all around the shape from one game to the next.
 * Exit: chosen from the remaining border regions with probability weighted by
 *   squared distance from the entry (far regions favoured, but every region
 *   keeps a chance), keeping a long solve path while still varying each carve.
 *
 * Candidates are filtered to only include regions whose centroid is inside the
 * contour (when contour is available). Entry and exit are always different
 * (when the pool has at least two regions). Both are drawn from the region list
 * passed in — in `recarveMaze` that is the set of DFS-visited regions, so entry
 * and exit are guaranteed to lie in the same connected component (solvable).
 */
export function pickEntryExit(
  regions: VoronoiRegion[],
  fullAdj: Map<number, Set<number>>,
  contour?: Position[],
): { entryRegionId: number; exitRegionId: number } {
  if (regions.length === 0) {
    return { entryRegionId: 0, exitRegionId: 0 };
  }

  if (regions.length === 1) {
    return { entryRegionId: regions[0].id, exitRegionId: regions[0].id };
  }

  void fullAdj; // border-ness is geometric (silhouette), not adjacency-based
  const borderIds = findSilhouetteBorderRegions(regions, contour);

  // Filter helper: region centroid must be inside contour (if contour exists)
  const hasValidContour = contour && contour.length > 2;
  const isInsideContour = (r: VoronoiRegion): boolean => {
    if (!hasValidContour) return true;
    // Use vertex centroid (more reliable than site for clipped polygons)
    const verts = r.vertices;
    if (verts.length === 0) return true;
    let sx = 0;
    let sy = 0;
    for (const v of verts) {
      sx += v.x;
      sy += v.y;
    }
    return pointInContour({ x: sx / verts.length, y: sy / verts.length }, contour!);
  };

  // Entry pool: silhouette-border regions (natural entrance on the edge of the
  // shape). Fall back progressively so we always have >= 2 candidates.
  const borderInside = regions.filter(
    (r) => borderIds.has(r.id) && isInsideContour(r),
  );
  const inside = regions.filter(isInsideContour);
  const entryPool =
    borderInside.length >= 2
      ? borderInside
      : inside.length >= 2
        ? inside
        : regions;

  // Entry: uniformly random across the whole border — moves every carve.
  const entryRegion = entryPool[Math.floor(Math.random() * entryPool.length)];

  // Exit: any inside region (border or deep), weighted toward regions far from
  // the entry (long solve path) but not argmax, so it varies each carve too.
  const exitPool = inside.length >= 2 ? inside : regions;
  const exitCandidates = exitPool.filter((r) => r.id !== entryRegion.id);
  if (exitCandidates.length === 0) {
    // Only one usable region — entry and exit collapse to the same region.
    return { entryRegionId: entryRegion.id, exitRegionId: entryRegion.id };
  }

  const exitRegion = weightedPick(
    exitCandidates,
    (r) =>
      (r.site.x - entryRegion.site.x) ** 2 +
      (r.site.y - entryRegion.site.y) ** 2 +
      1, // +1 so even close regions keep a non-zero chance
  );

  return { entryRegionId: entryRegion.id, exitRegionId: exitRegion.id };
}

/** Solve-path length (in cells) from `entry` to every reachable region through the carved passages. */
function bfsDistances(adj: Map<number, Set<number>>, entry: number): Map<number, number> {
  const dist = new Map<number, number>([[entry, 0]]);
  const queue = [entry];
  while (queue.length) {
    const cur = queue.shift()!;
    const d = dist.get(cur)!;
    for (const n of adj.get(cur) ?? []) {
      if (!dist.has(n)) {
        dist.set(n, d + 1);
        queue.push(n);
      }
    }
  }
  return dist;
}

/**
 * Choose the exit by SOLVE-PATH LENGTH through the carved maze:
 *   easy   — anywhere in the middle third of the reachable distances (short, varied),
 *   medium — weighted by distance² (far favoured, still varied),
 *   hard   — a random cell from the farthest 15 %,
 *   expert — the farthest cell (ties broken at random).
 * Always a region reachable from `entry` (distance finite) → the maze is solvable
 * by construction. `fallback` is used when nothing else is reachable.
 */
export function pickExitByDistance(
  mazeAdj: Map<number, Set<number>>,
  entry: number,
  difficulty: GameDifficulty,
  fallback: number,
  regions?: VoronoiRegion[],
): number {
  const dist = bfsDistances(mazeAdj, entry);
  let candidates = [...dist.entries()].filter(([id, d]) => id !== entry && d > 0);
  if (candidates.length === 0) return fallback;
  // Keep the exit VISIBLY apart from the entry too: a long solve path can still
  // end two cells away (the corridor loops back) and that reads as "start and
  // finish are next to each other". Require ≥ 40 % of the shape's diagonal on
  // screen; if nothing qualifies (tiny shapes), fall back to the unfiltered pool.
  if (regions && regions.length > 2) {
    const byId = new Map(regions.map((r) => [r.id, r] as const));
    const e = byId.get(entry);
    if (e) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const r of regions) {
        if (r.site.x < minX) minX = r.site.x;
        if (r.site.y < minY) minY = r.site.y;
        if (r.site.x > maxX) maxX = r.site.x;
        if (r.site.y > maxY) maxY = r.site.y;
      }
      const minSep = 0.4 * Math.hypot(maxX - minX, maxY - minY);
      const apart = candidates.filter(([id]) => {
        const r = byId.get(id);
        return !!r && Math.hypot(r.site.x - e.site.x, r.site.y - e.site.y) >= minSep;
      });
      if (apart.length > 0) candidates = apart;
    }
  }
  candidates.sort((a, b) => b[1] - a[1]);
  const max = candidates[0][1];
  const pickFrom = (pool: [number, number][]) => pool[Math.floor(Math.random() * pool.length)][0];
  switch (difficulty) {
    case 'expert': {
      return pickFrom(candidates.filter(([, d]) => d === max));
    }
    case 'hard': {
      const top = candidates.slice(0, Math.max(1, Math.ceil(candidates.length * 0.15)));
      return pickFrom(top);
    }
    case 'easy': {
      const lo = max / 3;
      const hi = (2 * max) / 3;
      const mid = candidates.filter(([, d]) => d >= lo && d <= hi);
      return pickFrom(mid.length ? mid : candidates);
    }
    default: {
      return weightedPick(candidates, ([, d]) => d * d)[0];
    }
  }
}

/**
 * BFS shortest path between two nodes in an adjacency graph.
 * Returns array of IDs from start to end (inclusive), or empty if unreachable.
 */
function bfsPath(
  adj: Map<number, Set<number>>,
  start: number,
  end: number,
): number[] {
  if (start === end) return [start];
  const visited = new Set<number>([start]);
  const parent = new Map<number, number>();
  const queue = [start];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === end) break;
    for (const nid of adj.get(current) ?? []) {
      if (!visited.has(nid)) {
        visited.add(nid);
        parent.set(nid, current);
        queue.push(nid);
      }
    }
  }

  if (!visited.has(end)) return [];

  const path: number[] = [];
  let cur = end;
  while (cur !== start) {
    path.push(cur);
    cur = parent.get(cur)!;
  }
  path.push(start);
  path.reverse();
  return path;
}

/**
 * Re-carve a Voronoi maze: randomize DFS, pick new entry/exit,
 * optionally add extra passages for easier difficulty.
 *
 * @param originalData - The original VoronoiMazeData (from template JSON).
 *                       This object is NOT mutated.
 * @param options      - Difficulty controls extra passage percentage.
 * @returns A new VoronoiMazeData with updated neighbors, entryRegionId, exitRegionId.
 */
export function recarveMaze(
  originalData: VoronoiMazeData,
  options?: RecarveOptions,
): VoronoiMazeData {
  const difficulty = options?.difficulty ?? 'medium';
  const extraPercent = DIFFICULTY_EXTRA_PERCENT[difficulty];

  // 1. Rebuild full adjacency from shared edges
  const fullAdj = buildFullAdjacency(originalData.regions);

  // 1b. Filter out edges that go outside the silhouette contour
  filterAdjacencyByContour(fullAdj, originalData.regions, originalData.contour);

  // 1c. Never open a passage too narrow to see / draw through. Cells that only
  //     touch their neighbours via slivers become closed (walled) cells — the
  //     carve, entry/exit and the solution all stay on the reachable component.
  pruneNarrowPassages(
    fullAdj,
    originalData.regions,
    options?.minPassageWidth ?? defaultMinPassageWidth(originalData.regions, fullAdj),
  );

  // 2. Run randomized DFS to create new spanning tree (weighted by edge length for wider passages)
  const mazeAdj = dfsCarve(fullAdj, originalData.regions);

  // 2b. Determine which regions DFS actually visited (graph may be disconnected after contour filtering)
  const dfsVisited = new Set<number>();
  for (const [id, neighbors] of mazeAdj) {
    if (neighbors.size > 0) {
      dfsVisited.add(id);
      for (const nid of neighbors) dfsVisited.add(nid);
    }
  }
  // If DFS visited nothing (single isolated nodes), add all region IDs
  if (dfsVisited.size === 0) {
    for (const id of mazeAdj.keys()) dfsVisited.add(id);
  }

  // 3. Add extra passages based on difficulty
  addExtraPassages(mazeAdj, fullAdj, extraPercent);

  // 4. Entry on the silhouette edge (DFS-visited regions only); exit by how far
  //    it is THROUGH THE CARVED MAZE, per difficulty (see pickExitByDistance) —
  //    a random exit next door made "expert" a two-turn stroll.
  const visitedRegions = originalData.regions.filter((r) => dfsVisited.has(r.id));
  const regionsForPick = visitedRegions.length > 0 ? visitedRegions : originalData.regions;
  const picked = pickEntryExit(regionsForPick, fullAdj, originalData.contour);
  const entryRegionId = picked.entryRegionId;
  const exitRegionId = pickExitByDistance(mazeAdj, entryRegionId, difficulty, picked.exitRegionId, regionsForPick);

  // 5. Build new regions with updated neighbor lists
  const newRegions: VoronoiRegion[] = originalData.regions.map((r) => ({
    ...r,
    vertices: [...r.vertices],
    neighbors: [...(mazeAdj.get(r.id) ?? [])],
  }));

  const result: VoronoiMazeData = {
    regions: newRegions,
    entryRegionId,
    exitRegionId,
    contour: originalData.contour
      ? originalData.contour.map((p) => ({ ...p }))
      : undefined,
    // carry the presentation metadata — a re-carve never changes geometry
    aspectRatio: originalData.aspectRatio,
    geometryTransform: originalData.geometryTransform ? { ...originalData.geometryTransform } : undefined,
  };

  // Validate: path must exist from entry to exit. If not, retry (up to 3 times).
  const path = findSolutionPath(result);
  if (path.length === 0 && (options?._retryCount ?? 0) < 3) {
    return recarveMaze(originalData, {
      ...options,
      difficulty: options?.difficulty ?? 'medium',
      _retryCount: (options?._retryCount ?? 0) + 1,
    });
  }

  // Retries exhausted — force-connect entry→exit through fullAdj (BFS shortest path)
  if (path.length === 0) {
    const forcePath = bfsPath(fullAdj, entryRegionId, exitRegionId);
    if (forcePath.length > 1) {
      const regionById = new Map<number, VoronoiRegion>();
      for (const r of result.regions) regionById.set(r.id, r);

      for (let i = 0; i < forcePath.length - 1; i++) {
        const a = forcePath[i];
        const b = forcePath[i + 1];
        const rA = regionById.get(a);
        const rB = regionById.get(b);
        if (rA && !rA.neighbors.includes(b)) rA.neighbors.push(b);
        if (rB && !rB.neighbors.includes(a)) rB.neighbors.push(a);
      }
    }
  }

  return result;
}

/**
 * Find the shortest path from entry to exit through DFS passages using BFS.
 * Returns an array of region IDs forming the path.
 * The renderer fills these regions to show the solution (no line = no wall crossing).
 * Returns empty array if no path exists.
 */
export function findSolutionPath(mazeData: VoronoiMazeData): number[] {
  const { regions, entryRegionId, exitRegionId } = mazeData;

  if (entryRegionId === exitRegionId) {
    return regions.some((r) => r.id === entryRegionId) ? [entryRegionId] : [];
  }

  // Build adjacency from neighbors
  const adj = new Map<number, Set<number>>();
  for (const r of regions) {
    adj.set(r.id, new Set(r.neighbors));
  }

  // BFS
  const visited = new Set<number>();
  const parent = new Map<number, number>();
  const queue = [entryRegionId];
  visited.add(entryRegionId);

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === exitRegionId) break;

    for (const nid of adj.get(current) ?? []) {
      if (!visited.has(nid)) {
        visited.add(nid);
        parent.set(nid, current);
        queue.push(nid);
      }
    }
  }

  if (!visited.has(exitRegionId)) return [];

  // Reconstruct path
  const regionById = new Map<number, VoronoiRegion>();
  for (const r of regions) regionById.set(r.id, r);

  const pathIds: number[] = [];
  let current = exitRegionId;
  while (current !== entryRegionId) {
    pathIds.push(current);
    const p = parent.get(current);
    if (p === undefined) return [];
    current = p;
  }
  pathIds.push(entryRegionId);
  pathIds.reverse();

  // Return region IDs (not positions) — renderer will fill regions
  return pathIds;
}
